/* ============================================================
   VIVĀRA — page shell: content painting, navigation, motion
   ------------------------------------------------------------
   Shared by every page. Every lookup is guarded so a page that lacks a
   component simply skips it. Runs after js/site-chrome.js so the injected
   header and footer are already in the DOM.

   Motion: Lenis inertial smoothing + masked line reveals + light parallax.
   Nothing is pinned; prefers-reduced-motion disables all of it.
   ============================================================ */
(function () {
  "use strict";

  /* Motion is registered AFTER every content module has injected its
     markup. main.js is the last script on the page, so its listener is
     the last one queued and every other module has already run. Without
     this, anything injected on DOMContentLoaded (testimonials, the
     specialist block) would never be picked up by a reveal and would sit
     at opacity 0 forever. */
  function boot() {

  var prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var isTouch = window.matchMedia("(hover: none), (pointer: coarse)").matches;
  var hasGsap = typeof window.gsap !== "undefined" && typeof window.ScrollTrigger !== "undefined";

  if (prefersReduced || !hasGsap) {
    document.documentElement.classList.add("no-motion");
  }

  /* ============================================================
     1 · CONTENT PAINTING (runs before motion so reveals bind to it)
     ============================================================ */

  function paintWhyLiquid() {
    var C = window.VIVARA_WHY_LIQUID;
    if (!C) return;

    var body = document.querySelector("[data-wl-body]");
    if (body) body.textContent = C.body;

    var qualities = document.querySelector("[data-wl-qualities]");
    if (qualities && C.qualities) {
      qualities.innerHTML = "";
      C.qualities.forEach(function (q) {
        var li = document.createElement("li");
        li.setAttribute("data-reveal", "");
        q.label.split("\n").forEach(function (line, i) {
          if (i) li.appendChild(document.createElement("br"));
          li.appendChild(document.createTextNode(line));
        });
        qualities.appendChild(li);
      });
    }

    var compare = document.querySelector("[data-wl-compare]");
    if (compare && C.comparison) {
      compare.innerHTML = "";

      var head = document.createElement("div");
      head.className = "wl-compare-head";
      head.setAttribute("data-reveal", "");
      C.comparison.columns.forEach(function (col) {
        var s = document.createElement("span");
        s.textContent = col;
        head.appendChild(s);
      });
      compare.appendChild(head);

      var rows = document.createElement("ul");
      rows.className = "wl-compare-rows";
      C.comparison.rows.forEach(function (row) {
        var li = document.createElement("li");
        li.setAttribute("data-reveal", "");

        var label = document.createElement("span");
        label.className = "wl-row-label";
        label.textContent = row.label;
        li.appendChild(label);

        [row.liquid, row.other].forEach(function (on) {
          var dot = document.createElement("i");
          dot.className = "wl-dot" + (on ? " wl-dot--on" : "");
          dot.setAttribute("aria-hidden", "true");
          li.appendChild(dot);
        });

        rows.appendChild(li);
      });
      compare.appendChild(rows);

    }
  }

  paintWhyLiquid();

  /* ============================================================
     2 · HEADER + NAVIGATION
     ============================================================ */
  var header = document.getElementById("siteHeader");
  var themedSections = Array.prototype.slice.call(document.querySelectorAll("[data-theme]"));

  function updateHeader() {
    if (!header) return;
    var y = window.scrollY || window.pageYOffset;
    header.classList.toggle("is-scrolled", y > 40);

    var probe = header.offsetHeight * 0.5;
    var current = null;
    for (var i = 0; i < themedSections.length; i++) {
      var r = themedSections[i].getBoundingClientRect();
      if (r.top <= probe && r.bottom >= probe) {
        current = themedSections[i];
        break;
      }
    }
    header.classList.toggle("on-light", !!current && current.getAttribute("data-theme") === "light");
  }
  updateHeader();

  /* --- in-page anchors -----------------------------------------------
     The markup uses real href="#id" links, so with no JavaScript the
     browser still jumps to the section and CSS scroll-margin-top keeps it
     clear of the fixed header. Here we upgrade that to a smooth scroll
     through Lenis when it is running, and honour reduced motion. */
  function scrollToTarget(el) {
    if (!el) return;
    var offset = (header ? header.offsetHeight : 0) + 8;
    var top = el.getBoundingClientRect().top + (window.scrollY || window.pageYOffset) - offset;
    if (prefersReduced) {
      window.scrollTo(0, top);
    } else if (window.__vivaraLenis) {
      window.__vivaraLenis.scrollTo(top, { duration: 1.15 });
    } else {
      window.scrollTo({ top: top, behavior: "smooth" });
    }
  }

  document.querySelectorAll('a[data-scroll-to][href^="#"]').forEach(function (link) {
    link.addEventListener("click", function (e) {
      var id = link.getAttribute("href").slice(1);
      var target = document.getElementById(id);
      if (!target) return;                 /* let the browser handle it */
      e.preventDefault();
      scrollToTarget(target);
      /* keep the address bar and back button honest */
      if (window.history && window.history.pushState) {
        window.history.pushState(null, "", "#" + id);
      }
      target.setAttribute("tabindex", "-1");
      target.focus({ preventScroll: true });
    });
  });

  /* --- mobile overlay menu --- */
  var toggle = document.querySelector(".nav-toggle");
  var menu = document.getElementById("mobileMenu");

  function setMenu(open) {
    if (!toggle || !menu) return;
    toggle.setAttribute("aria-expanded", open ? "true" : "false");
    toggle.setAttribute("aria-label", open ? "Close menu" : "Open menu");
    document.documentElement.classList.toggle("menu-open", open);
    if (open) {
      menu.hidden = false;
      requestAnimationFrame(function () {
        menu.classList.add("is-open");
      });
      if (window.__vivaraLenis) window.__vivaraLenis.stop();
    } else {
      menu.classList.remove("is-open");
      if (window.__vivaraLenis) window.__vivaraLenis.start();
      window.setTimeout(function () {
        if (!menu.classList.contains("is-open")) menu.hidden = true;
      }, 360);
    }
  }

  if (toggle && menu) {
    toggle.addEventListener("click", function () {
      setMenu(toggle.getAttribute("aria-expanded") !== "true");
    });
    menu.addEventListener("click", function (e) {
      if (e.target.tagName === "A") setMenu(false);
    });
    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape") setMenu(false);
    });
  }

  /* ============================================================
     3 · SMOOTH SCROLL (Lenis) + anchors
     ============================================================ */
  var lenis = null;
  if (!prefersReduced && !isTouch && typeof window.Lenis !== "undefined") {
    lenis = new Lenis({
      lerp: 0.13,
      wheelMultiplier: 1,
      smoothWheel: true,
      syncTouch: false
    });
    window.__vivaraLenis = lenis;

    if (hasGsap) {
      lenis.on("scroll", ScrollTrigger.update);
      gsap.ticker.add(function (time) {
        lenis.raf(time * 1000);
      });
      gsap.ticker.lagSmoothing(0);
    } else {
      (function raf(t) {
        lenis.raf(t);
        requestAnimationFrame(raf);
      })(0);
    }
    lenis.on("scroll", updateHeader);
  } else {
    window.addEventListener("scroll", updateHeader, { passive: true });
  }

  /* ------------------------------------------------------------
     FAQ page → FAQPage markup.

     The store's FAQ lives in a Shopify page body, which Liquid cannot
     parse into structured data. The heading-and-answer pairs are read
     from the rendered page instead and published as FAQPage, which is
     what puts these answers in front of an answer engine rather than
     leaving them as prose nobody quotes.

     Deliberately narrow: only on an editorial page whose own title is
     a questions page, and only when it really is a run of headings
     each followed by prose. Anything else is left alone.
     ------------------------------------------------------------ */
  (function faqSchema() {
    var prose = document.querySelector("[data-editorial-prose]");
    if (!prose || document.getElementById("vivara-faq-schema")) return;

    var heading = document.querySelector(".editorial-title");
    var title = (heading && heading.textContent || "").toLowerCase();
    if (title.indexOf("question") === -1 && title.indexOf("faq") === -1) return;

    var entries = [];
    [].forEach.call(prose.querySelectorAll("h2, h3"), function (h) {
      var q = (h.textContent || "").trim();
      if (!q) return;
      var answer = "", node = h.nextElementSibling;
      while (node && !/^H[23]$/.test(node.tagName)) {
        if (node.textContent) answer += (answer ? " " : "") + node.textContent.trim();
        node = node.nextElementSibling;
      }
      answer = answer.replace(/\s+/g, " ").trim();
      if (answer.length > 20) entries.push({ q: q, a: answer });
    });
    if (entries.length < 3) return;

    var doc = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: entries.map(function (e) {
        return { "@type": "Question", name: e.q,
                 acceptedAnswer: { "@type": "Answer", text: e.a } };
      })
    };
    var s = document.createElement("script");
    s.type = "application/ld+json";
    s.id = "vivara-faq-schema";
    s.textContent = JSON.stringify(doc);
    document.head.appendChild(s);
  })();

  /* ------------------------------------------------------------
     Editorial index for Shopify-rendered pages.

     The static pages ship a sticky contents list beside the prose.
     A Shopify page arrives as one block of RTE HTML, so the list is
     built from its own headings here. Without JavaScript the prose
     is the only child of the grid and the stylesheet gives it the
     full width, so nothing depends on this running.
     ------------------------------------------------------------ */
  (function buildEditorialIndex() {
    var prose = document.querySelector("[data-editorial-prose]");
    if (!prose) return;
    var body = prose.parentNode;
    if (!body || body.querySelector(".editorial-index")) return;

    var heads = prose.querySelectorAll("h2");
    if (heads.length < 2) return;

    var nav = document.createElement("nav");
    nav.className = "editorial-index";
    nav.setAttribute("aria-label", "On this page");
    var label = document.createElement("p");
    label.textContent = "Contents";
    nav.appendChild(label);

    var used = {};
    [].forEach.call(heads, function (h) {
      var text = (h.textContent || "").trim();
      if (!text) return;
      var id = h.id;
      if (!id) {
        id = text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 48);
        if (!id) return;
        while (used[id] || document.getElementById(id)) id += "-x";
        h.id = id;
      }
      used[id] = true;
      var a = document.createElement("a");
      a.href = "#" + id;
      a.textContent = text;
      nav.appendChild(a);
    });

    if (nav.children.length > 1) body.insertBefore(nav, prose);
  })();

  document.querySelectorAll('a[href^="#"]').forEach(function (a) {
    a.addEventListener("click", function (e) {
      var id = a.getAttribute("href");
      if (!id || id.length < 2) return;
      var target = document.querySelector(id);
      if (!target) return;
      e.preventDefault();
      if (lenis) lenis.scrollTo(target, { offset: 0, duration: 1.4 });
      else target.scrollIntoView({ behavior: prefersReduced ? "auto" : "smooth" });
    });
  });

  if (prefersReduced || !hasGsap) return;

  /* ============================================================
     4 · MOTION — restrained reveals + light parallax
     ============================================================ */
  gsap.registerPlugin(ScrollTrigger);
  var EASE = "power3.out";

  document.querySelectorAll("[data-lines]").forEach(function (block) {
    gsap.to(block.querySelectorAll(".rline-inner"), {
      y: 0,
      duration: 1.15,
      ease: EASE,
      stagger: 0.13,
      scrollTrigger: { trigger: block, start: "top 86%", once: true }
    });
  });

  document.querySelectorAll("[data-reveal-group]").forEach(function (group) {
    gsap.to(group.querySelectorAll("[data-reveal]"), {
      opacity: 1,
      y: 0,
      duration: 1,
      ease: EASE,
      stagger: 0.09,
      scrollTrigger: { trigger: group, start: "top 85%", once: true }
    });
  });

  document.querySelectorAll("[data-reveal]").forEach(function (el) {
    if (el.closest("[data-reveal-group]")) return;
    gsap.to(el, {
      opacity: 1,
      y: 0,
      duration: 1,
      delay: 0.12,
      ease: EASE,
      scrollTrigger: { trigger: el, start: "top 90%", once: true }
    });
  });

  var heroBg = document.querySelector("[data-hero-bg] img");
  if (heroBg) {
    gsap.fromTo(heroBg, { scale: 1.025 }, { scale: 1, duration: 2.2, ease: "power2.out" });
    gsap.to(heroBg, {
      yPercent: 6,
      ease: "none",
      scrollTrigger: { trigger: ".hero", start: "top top", end: "bottom top", scrub: true }
    });
  }

  document.querySelectorAll("[data-parallax]").forEach(function (frame) {
    var img = frame.querySelector("img") || frame;
    var amount = frame.classList.contains("mineral-photo") || frame.classList.contains("rec-photo") ? 5 : 8;
    gsap.fromTo(img, { yPercent: -amount * 0.5 }, {
      yPercent: amount * 0.5,
      ease: "none",
      scrollTrigger: { trigger: frame, start: "top bottom", end: "bottom top", scrub: true }
    });
  });

  /* Each soft-parallax element is driven by its own section, so the same
     behaviour works wherever the element is used. Hard-coding one section
     here logged "Element not found" on any page that lacked it. */
  document.querySelectorAll("[data-parallax-soft]").forEach(function (el) {
    var trigger = el.closest("section") || el;
    gsap.fromTo(el, { yPercent: -2 }, {
      yPercent: 2,
      ease: "none",
      scrollTrigger: { trigger: trigger, start: "top bottom", end: "bottom top", scrub: true }
    });
  });

  ScrollTrigger.refresh();

  /* ------------------------------------------------------------
     Reveal failsafe.

     Every reveal is measured at boot, while images below the fold
     still have no height. When they load, the page grows and a
     trigger that was measured against the short page can sit past
     its own start without ever firing — the element then stays at
     opacity 0 for the life of the page. That is how the Join Our
     Community block came to render as an empty black band.

     Two guards: re-measure whenever the page actually changes size,
     and, once loading has settled, force anything still hidden but
     already scrolled past into its finished state. Content is never
     allowed to be invisible for want of an animation.
     ------------------------------------------------------------ */
  /* The pending lists are collected once. Querying the whole document and
     reading layout on every scroll frame is what made the reveals stutter:
     several hundred getBoundingClientRect calls per frame forces a synchronous
     layout each time, and the frames that overran were the ones an animation
     was mid-way through. Each element leaves the list as soon as it is
     settled, so the work shrinks to nothing as the page is read. */
  var pendingReveals = [].slice.call(document.querySelectorAll("[data-reveal]"));
  var pendingLines = [].slice.call(document.querySelectorAll("[data-lines] .rline"));

  function sweep() {
    var limit = window.innerHeight * 0.8;

    pendingReveals = pendingReveals.filter(function (el) {
      if (!el.isConnected) return false;
      if (el.getBoundingClientRect().top > limit) return true;
      if (gsap.isTweening(el)) return true;
      if (window.getComputedStyle(el).opacity === "0") gsap.set(el, { opacity: 1, y: 0 });
      return false;
    });

    pendingLines = pendingLines.filter(function (box) {
      if (!box.isConnected) return false;
      if (box.getBoundingClientRect().top > limit) return true;
      var inner = box.querySelector(".rline-inner");
      if (!inner) return false;
      if (gsap.isTweening(inner)) return true;
      if (inner.getBoundingClientRect().top - box.getBoundingClientRect().top > 2) {
        gsap.set(inner, { y: 0 });
      }
      return false;
    });
  }

  window.addEventListener("load", function () {
    ScrollTrigger.refresh();
    sweep();
    window.setTimeout(function () { ScrollTrigger.refresh(); sweep(); }, 400);
    window.setTimeout(function () { ScrollTrigger.refresh(); sweep(); }, 1400);
  });

  /* A jump — an anchor, a restored scroll position, a screenshot tool, a
     find-in-page — moves the viewport without the wheel events ScrollTrigger
     was watching for, and whatever it lands on can still be at opacity 0. The
     check runs once the scroll has stopped, so nothing competes with a
     running animation for the frame. */
  var idle = null, scrolling = false;
  window.addEventListener("scroll", function () {
    scrolling = true;
    if (idle) window.clearTimeout(idle);
    idle = window.setTimeout(function () { scrolling = false; sweep(); }, 160);
  }, { passive: true });

  /* Lazy images change the page height as they arrive, and every change
     invalidates the trigger positions. Refreshing on each one — mid-scroll —
     cost whole frames, so it is debounced and held until the page is still. */
  if (typeof ResizeObserver !== "undefined") {
    var last = document.body.scrollHeight, timer = null;
    function settle() {
      /* strict mode has no arguments.callee, and waiting out an active scroll
         needs to re-arm itself */
      if (scrolling) { timer = window.setTimeout(settle, 200); return; }
      ScrollTrigger.refresh();
      sweep();
    }
    var ro = new ResizeObserver(function () {
      if (Math.abs(document.body.scrollHeight - last) < 4) return;
      last = document.body.scrollHeight;
      if (timer) window.clearTimeout(timer);
      timer = window.setTimeout(settle, 300);
    });
    ro.observe(document.body);
  }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
